import {
  Button,
  Drawer,
  Icon,
  Offscreen,
  Panel,
  SectionHeader
} from '@deque/cauldron-react';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { type MemberFederation } from '~/modules/app/hooks/useMemberFederation';
import Separator from '~/modules/common/components/Separator';
import { useAuthContext } from '~/modules/common/contexts/auth';
import { useFeatureFlagState } from '~/modules/common/contexts/featureFlags';
import { useProducts } from '~/modules/common/contexts/products';
import { type EnterpriseMembers } from '~/modules/common/utils/billing-client/client-v2';
import { formatLongDateTime } from '~/modules/common/utils/dates';
import styles from './ViewAcceptedMemberDetails.css';

interface ViewAcceptedMemberDetailsProps {
  member: EnterpriseMembers.AcceptedMember;
  onViewDetailsClose: () => void;
  onRemoveClick: (member: EnterpriseMembers.AcceptedMember) => void;
  onEditEmailClick: (member: EnterpriseMembers.AcceptedMember) => void;
  memberFederation?: MemberFederation;
}

function ViewAcceptedMemberDetails({
  member,
  onViewDetailsClose,
  onRemoveClick,
  onEditEmailClick,
  memberFederation
}: ViewAcceptedMemberDetailsProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const enterpriseEmailRestrictionEnabled = useFeatureFlagState(
    'restrict_enterprise_email_changes_v1'
  );
  const userActivityEnabled = useFeatureFlagState('user_activity_v1');
  const { user } = useAuthContext();
  const { getProductBySlug } = useProducts();
  const history = useHistory();

  const isCurrentUser = user?.id === member.user_id;
  const memberName = [member.first_name, member.last_name]
    .filter(Boolean)
    .join(' ');
  const federationStatus = memberFederation?.statusFor(member.user_id);
  const canEditEmail =
    enterpriseEmailRestrictionEnabled &&
    (!memberFederation || federationStatus === 'unfederated');

  const describeFederationStatus = () => {
    switch (federationStatus) {
      case 'unfederated':
        return t("This user's email can be edited.");
      case 'federated':
        return t("This user's email is managed by their identity provider.");
      case 'error':
        return t(
          "We couldn't check whether this user's email can be edited. Close and reopen the details to try again."
        );
      default:
        return t("Checking whether this user's email can be edited…");
    }
  };
  const federationMessage = describeFederationStatus();

  const formattedProductActivity = (productSlug: string) => {
    const productActivity = member.user_activity.find(
      activity => activity.product_slug === productSlug
    );
    if (!productActivity) {
      return (
        <div className={styles.productActivity}>
          <Icon type="question-circle-alt" />
          {t('Activity not tracked')}
        </div>
      );
    }

    const formattedDate = formatLongDateTime(productActivity.updated_at);

    return <div>{t('Last active: {{date}}', { date: formattedDate })}</div>;
  };

  return (
    <>
      <Drawer
        open={!!member}
        onClose={onViewDetailsClose}
        position="right"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={styles.drawer}
      >
        <Panel className={styles.panel}>
          <Button variant="secondary" onClick={onViewDetailsClose}>
            <Icon type="close" />
            {t('Close')}
          </Button>
          <Separator />
          <SectionHeader
            heading={<h2 id={headingId}>{memberName || member.email}</h2>}
            description={memberName ? member.email : undefined}
          />
          {memberFederation && (
            <div role="status">
              {federationStatus === 'unfederated' ? (
                <Offscreen>{federationMessage}</Offscreen>
              ) : (
                <p>{federationMessage}</p>
              )}
            </div>
          )}
          {canEditEmail && (
            <Button
              variant="secondary"
              className={styles.editEmailButton}
              onClick={() => onEditEmailClick(member)}
            >
              {t('Edit email')}
            </Button>
          )}
          <Separator />
          <SectionHeader
            heading={
              <h3>
                {userActivityEnabled
                  ? t('Product access and activity')
                  : t('Product access')}
              </h3>
            }
          />
          {userActivityEnabled && (
            <p>
              {t(
                'Not tracked means we cannot determine if or when the product was accessed. Verify usage before removing access.'
              )}
            </p>
          )}
          <div className={styles.productAccessContainer}>
            {member.product_access.map(access => (
              <Panel key={access.product_slug}>
                <h4>{getProductBySlug(access.product_slug)?.name}</h4>
                {userActivityEnabled && (
                  <div>{formattedProductActivity(access.product_slug)}</div>
                )}
              </Panel>
            ))}
            <Panel>
              <h4>
                {t('Axe Account')} {member.is_admin && t('(admin)')}
              </h4>
              {userActivityEnabled && (
                <div>{formattedProductActivity('axe-account')}</div>
              )}
            </Panel>
          </div>
          <Button
            variant="secondary"
            onClick={() =>
              history.push(`/user-access/edit-user/${member.user_id}`)
            }
          >
            {t('Edit Access')}
          </Button>
          {!isCurrentUser && (
            <>
              <Separator />
              <SectionHeader heading={<h3>{t('Actions')}</h3>} />
              <div className={styles.actionsContainer}>
                <Button
                  variant="danger-secondary"
                  onClick={() => onRemoveClick(member)}
                >
                  {t('Remove user')}
                </Button>
              </div>
            </>
          )}
        </Panel>
      </Drawer>
    </>
  );
}

export default ViewAcceptedMemberDetails;
